import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatAppointmentResponseBody,
  isSuccessfulAppointmentResponse,
  readAppointmentResponseBody,
} from '../appointmentResponse';
import { AppointmentResponseErrorDialog } from '../components/AppointmentResponseErrorDialog';
import type { AppointmentFull } from '../backendInterfaces';
import { API_BASE_URL } from '../constants';
import { t, type TranslationKey } from '../i18n/translations';
import type { FullUser } from './ProfilePage';
import { AdminPatientSearch } from '../components/AdminPatientSearch';
import './AdminBookingPage.css';

const categoryItems = ['control', 'filling', 'surgery', 'endo', 'prosthetics', 'ortho', 'exam', 'various', 'proflaxis'];
const advancedOptionItems = ['Option A', 'Option B', 'Option C', 'Option D'];

//const practitionerUserIds = ['data_c_user_10', 'data_c_user_11', 'data_c_user_12', 'data_c_user_13', 'data_c_user_14', 'data_c_user_15'];
const practitionerIds = ['data_c_10', 'data_c_11', 'data_c_12', 'data_c_13', 'data_c_14', 'data_c_15'];

//const practitionerUserIds = ['data_c_user_10', 'data_c_user_11', 'data_c_user_12'];
//const practitionerIds = ['data_c_10', 'data_c_11', 'data_c_12'];

const placeholder = (action: string) => {
  console.log(`placeholder: ${action}`);
};

const CalendarIcon = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3.5" y="4.5" width="13" height="12" rx="2.5" />
    <path d="M6.5 2.8v3.4" strokeLinecap="round" />
    <path d="M13.5 2.8v3.4" strokeLinecap="round" />
    <path d="M3.5 8.5h13" strokeLinecap="round" />
  </svg>
);

export interface Procedure {
  id: string;
  customerId: string;
  name: string;
  category: string;
  type: string;
  duration: number;
  [ruleMap: string]: any;
}

interface FullPractitioner {
  // the FullUser fields:
  //id: string; // DEPRECATED, use userId and do not mix up with practitionerId below...
  userId: string;
  customerId: string;
  userName: string;
  firstName: string;
  familyName: string;
  email: string;
  mobile: string;
  userDetailId: string;
  extId: string;
  salutation: string;
  title: string;
  zipCode: string;
  town: string;
  addressLines: string[];
  languages: string[];
  dateOfBirth: string;
  comment: string;
  // the fields from Practitioner:
  practitionerId: string;
  officeId: string;
  practitionerType: string;
  procedureIds: string[];
  photo: string;
  //practitionerInfo: JSON; // not yet
}

const practitionerCache = new Map<string, FullPractitioner>();
const adminBookingAgendaSelectionKey = 'adminBookingAgendaSelection';

const getNameInitials = (firstName?: string) =>
  String(firstName ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((namePart) => `${namePart.charAt(0)}.`)
    .join(' ');

const getPractitionerDisplayName = (practitioner?: FullPractitioner) => {
  if (!practitioner) return '';

  const title = String(practitioner.title ?? '').trim();
  const initials = getNameInitials(String(practitioner.firstName ?? ''));
  const familyName = String(practitioner.familyName ?? '').trim();

  return [title, initials, familyName].filter(Boolean).join(' ').trim();
};

const parseDateInput = (value: string) => {
  const parts = value.trim().split('.');
  if (parts.length !== 3) {
    return null;
  }

  const day = Number(parts[0]);
  const month = Number(parts[1]);
  const year = Number(parts[2]);
  const parsed = new Date(year, month - 1, day);

  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }

  return parsed;
};

const formatEditableDate = (date: Date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return day + '.' + month + '.' + year;
};

const formatLocalDateTime = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return year + '-' + month + '-' + day + 'T' + hours + ':' + minutes + ':' + seconds;
};

const readStoredSelectedDateTime = () => {
  const raw = localStorage.getItem('selectedDateTime');
  if (!raw) {
    return null;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
};

const readStoredSelectedPractitionerId = () => {
  const storedPractitionerId = localStorage.getItem('selectedPractitionerId');
  return storedPractitionerId && practitionerIds.includes(storedPractitionerId) ? storedPractitionerId : '';
};

const padTimePart = (value: number) => value.toString().padStart(2, '0');

const normalizeTimeInput = (value: string) => {
  const match = value.trim().match(/^([0-9]{1,2}):([0-9]{1,2})$/);
  if (!match) {
    return value;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return value;
  }

  return `${padTimePart(hours)}:${padTimePart(minutes)}`;
};

const parseTimeToMinutes = (value: string) => {
  const match = value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
};

const formatMinutesAsTime = (totalMinutes: number) => {
  const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${padTimePart(hours)}:${padTimePart(minutes)}`;
};

const normalizeDurationInput = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const [hoursPart = '', minutesPart = ''] = trimmed.split(':');
  if (!/^\d{1,2}$/.test(hoursPart) || !/^\d{1,2}$/.test(minutesPart)) {
    return value;
  }

  const hours = Number(hoursPart);
  const minutes = Number(minutesPart);
  if (hours < 0 || minutes < 0 || minutes > 59) {
    return value;
  }

  return `${padTimePart(hours)}:${padTimePart(minutes)}`;
};

const parseDurationToMinutes = (value: string) => {
  const match = value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || minutes < 0 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
};

type DropdownFieldProps = {
  label?: string;
  placeholderText: string;
  action: string;
  items: string[];
  fullWidth?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  getItemLabel?: (item: string) => string;
  allowEmptySelection?: boolean;
  compactValueLabel?: string;
};

function DropdownField({
  label,
  placeholderText,
  action,
  items,
  fullWidth = false,
  value,
  onChange,
  disabled = false,
  getItemLabel,
  allowEmptySelection = false,
  compactValueLabel,
}: DropdownFieldProps) {
  return (
    <label className={`flex flex-col gap-2.5 ${fullWidth ? 'w-full' : ''}`}>
      {label ? (
        <span className="text-sm font-semibold tracking-[0.08em] text-slate-500">
          {label}
        </span>
      ) : null}
      <div className={`relative${compactValueLabel ? ' admin-booking__compact-select' : ''}`}>
        <select
          aria-label={label}
          title={compactValueLabel && value && getItemLabel ? getItemLabel(value) : undefined}
          value={value ?? ''}
          disabled={disabled}
          onChange={(event) => {
            const nextValue = event.target.value;
            placeholder(`${action}-${nextValue}`);
            onChange?.(nextValue);
          }}
          className="h-13 w-full appearance-none rounded-2xl border border-[#efe7da] bg-white px-5 pr-14 text-[17px] text-slate-700 outline-none transition focus:border-[#cbb58a] disabled:cursor-not-allowed disabled:bg-[#fbf9f5] disabled:text-slate-400"
        >
          <option value="" disabled={!allowEmptySelection}>
            {placeholderText}
          </option>
          {items.map((item) => (
            <option key={item} value={item}>
              {getItemLabel ? getItemLabel(item) : item}
            </option>
          ))}
        </select>
        {compactValueLabel ? <span className="admin-booking__practitioner-initials" aria-hidden="true">{compactValueLabel}</span> : null}
        <span className="pointer-events-none absolute inset-y-0 right-5 flex items-center text-slate-400">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
    </label>
  );
}

type SmallButtonProps = {
  children: string;
  action: string;
  onClick?: () => void;
  disabled?: boolean;
};

function SmallButton({ children, action, onClick, disabled = false }: SmallButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        placeholder(action);
        onClick?.();
      }}
      className="rounded-full border border-[#efe7da] bg-white px-4 py-2 text-sm font-medium text-slate-500 transition hover:border-[#cbb58a] hover:text-slate-800 disabled:cursor-not-allowed disabled:bg-[#fbf9f5] disabled:text-slate-400"
    >
      {children}
    </button>
  );
}

type UtilityButtonProps = {
  children: string;
  action: string;
  icon: ReactNode;
};

function UtilityButton({ children, action, icon }: UtilityButtonProps) {
  return (
    <button
      type="button"
      onClick={() => placeholder(action)}
      className="flex h-11 min-w-[132px] items-center justify-center gap-2.5 rounded-2xl border border-[#e5dccd] bg-[#fbf7f1] px-5 text-base font-medium text-[#c4a67b] transition hover:border-[#cbb58a] hover:text-[#b79a62]"
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}

const translateProcedureName = (procedureName?: string) => {
  if (!procedureName) return 'Behandlung';
  const translationKey = ('pro-' + procedureName) as TranslationKey;
  const translatedName = t(translationKey) || procedureName;
  return translatedName.charAt(0).toUpperCase() + translatedName.slice(1);
};

const translateCategoryName = (categoryName?: string) => {
  if (!categoryName) return 'Kategorie';
  if (categoryName === 'proflaxis') return 'Prophylaxe';
  const translatedName = t(categoryName as TranslationKey) || categoryName;
  return translatedName.charAt(0).toUpperCase() + translatedName.slice(1);
};

export function AdminBookingPage() {
  const navigate = useNavigate();
  const initialSelectedDateTime = readStoredSelectedDateTime();
  const [selectedAppointmentId] = useState(() => localStorage.getItem('selectedAppointmentId')?.trim() || '');
  const [existingAppointmentStatus, setExistingAppointmentStatus] = useState('unconfirmed');
  const [editingProcedureId, setEditingProcedureId] = useState('');
  const [appointmentLoading, setAppointmentLoading] = useState(Boolean(selectedAppointmentId));
  const [selectedPatient, setSelectedPatient] = useState<FullUser | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTreatment, setSelectedTreatment] = useState('');
  const [appointmentDescription, setAppointmentDescription] = useState('');
  const [selectedPractitionerId, setSelectedPractitionerId] = useState(readStoredSelectedPractitionerId);
  const [appointmentDate, setAppointmentDate] = useState(() =>
    formatEditableDate(initialSelectedDateTime ?? new Date())
  );
  const [appointmentStartTime, setAppointmentStartTime] = useState(() =>
    initialSelectedDateTime
      ? padTimePart(initialSelectedDateTime.getHours()) + ':' + padTimePart(initialSelectedDateTime.getMinutes())
      : '09:00'
  );
  const [appointmentDuration, setAppointmentDuration] = useState('00:30');
  const [hasManualDurationOverride, setHasManualDurationOverride] = useState(false);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [practitionersById, setPractitionersById] = useState<Record<string, FullPractitioner>>({});
  const [practitionersLoading, setPractitionersLoading] = useState(false);
  const [practitionersError, setPractitionersError] = useState('');
  const [treatmentsLoading, setTreatmentsLoading] = useState(false);
  const [treatmentsError, setTreatmentsError] = useState('');

  const [saveErrorMessage, setSaveErrorMessage] = useState('');

  // Filter only the new-appointment picker; keep backend data and existing assignments intact.
  const selectablePractitionerIds = useMemo(() => practitionerIds.filter((practitionerId) => {
    if (selectedAppointmentId) return true;
    const practitioner = practitionersById[practitionerId];
    const familyName = String(practitioner?.familyName ?? '').trim().normalize('NFC').toLowerCase();
    const firstInitial = String(practitioner?.firstName ?? '').trim().charAt(0).toLowerCase();
    return !(familyName === 'müller' && firstInitial === 's');
  }), [practitionersById, selectedAppointmentId]);
  const selectableSelectedPractitionerId = selectablePractitionerIds.includes(selectedPractitionerId) ? selectedPractitionerId : '';

  const selectedProcedureType = 'single';

  const treatmentItems = useMemo(() => {
    return procedures
      .map((procedure) => procedure?.name?.trim())
      .filter((name): name is string => Boolean(name));
  }, [procedures]);

  const selectedProcedure = useMemo(
    () => procedures.find((procedure) => procedure?.name?.trim() === selectedTreatment),
    [procedures, selectedTreatment]
  );
  const hasValidDateAndTime = Boolean(parseDateInput(appointmentDate)) &&
    parseTimeToMinutes(appointmentStartTime) !== null &&
    (parseDurationToMinutes(appointmentDuration) ?? 0) > 0;
  const isSaveDisabled = !selectedPatient || !selectableSelectedPractitionerId || !selectedProcedure || !hasValidDateAndTime;

  const handleSelectAgendaSlot = (appointmentId: string) => {
    localStorage.setItem(adminBookingAgendaSelectionKey, appointmentId || 'new');

    if (appointmentId) {
      localStorage.setItem('selectedAppointmentId', appointmentId);
    }

    const selectedDateTime = localStorage.getItem('selectedDateTime');
    if (selectedDateTime) {
      localStorage.setItem('agendaDate', selectedDateTime);
    }

    navigate('/agenda');
  };

  const computedEndTime = useMemo(() => {
    const startMinutes = parseTimeToMinutes(appointmentStartTime);
    const durationMinutes = parseDurationToMinutes(appointmentDuration);

    if (startMinutes === null || durationMinutes === null) {
      return '';
    }

    return formatMinutesAsTime(startMinutes + durationMinutes);
  }, [appointmentDuration, appointmentStartTime]);


  const saveAppointment = async () => {
    setSaveErrorMessage('');

    const when = localStorage.getItem('selectedDateTime');
    const patientId = localStorage.getItem('selectedPatientId');
    //const patientId = selectedPatient?.id; // TODO: this returns the userId, WHY???????
    const procedureId = selectedProcedure?.id;
    const token = localStorage.getItem('token');
    const duration = parseDurationToMinutes(appointmentDuration);

    console.log("saveAppointment: " + when + " " + duration + " " + patientId + " " + selectedPractitionerId + " " + procedureId);
    console.log("   " + JSON.stringify(selectedPatient));
    console.log("   " + localStorage.getItem('selectedPatientId'));

    if (appointmentLoading || isSaveDisabled || !when || !patientId || !procedureId || duration === null || duration <= 0) {
      return;
    }

    try {
      // TODO: always save to Ergodent also:
      const response = await fetch('/api/appointments/save-full', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: 'Bearer ' + token } : {}),
        },
        body: JSON.stringify({
          ...(selectedAppointmentId ? { id: selectedAppointmentId } : {}),
          when,
          duration,
          practitionerId: selectedPractitionerId,
          patientId,
          procedureId,
          description: appointmentDescription,
          status: existingAppointmentStatus,
          mode: "ergodent:demo",
        }),
      });

      const responseBody = await readAppointmentResponseBody(response);
      const saveSuccessful = isSuccessfulAppointmentResponse(response, responseBody);

      if (!saveSuccessful) {
        setSaveErrorMessage(`Error: ${formatAppointmentResponseBody(responseBody)}`);
        return;
      }
    } catch (error) {
      console.error('Failed to save appointment', error);
      setSaveErrorMessage(`Error: ${error instanceof Error ? error.message : String(error)}`);
      return;
    }

    localStorage.removeItem('selectedAppointmentId');
    navigate('/agenda');
  };

  useEffect(() => {
    if (!selectedAppointmentId) {
      return;
    }

    let isMounted = true;

    console.log("selectedAppointmentId=" + selectedAppointmentId);

    const fetchExistingAppointment = async () => {
      const token = localStorage.getItem('token');

      console.log("selectedAppointmentId=" + selectedAppointmentId);

      try {
        const appointmentResponse = await fetch(
          `${API_BASE_URL}/appointments/get-full-appointment/${selectedAppointmentId}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );

        if (!appointmentResponse.ok) {
          throw new Error(`Bad appointment response: ${appointmentResponse.status}`);
        }

        const appointment = (await appointmentResponse.json()) as AppointmentFull;
        const patientId = appointment.patientIds?.[0]?.trim() || '';
        const procedureId = appointment.procedureIds?.[0]?.trim() || '';
        const practitionerId = appointment.practitionerIds?.[0]?.trim() || '';
        const appointmentWhen = new Date(appointment.when);

        const [patient, procedure] = await Promise.all([
          patientId
            ? fetch(`${API_BASE_URL}/patients/full/${patientId}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            }).then(async (response) => {
              if (!response.ok) {
                throw new Error(`Bad patient response: ${response.status}`);
              }
              return (await response.json()) as FullUser;
            })
            : Promise.resolve(null),
          procedureId
            ? fetch(`${API_BASE_URL}/procedures/${procedureId}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            }).then(async (response) => {
              if (!response.ok) {
                throw new Error(`Bad procedure response: ${response.status}`);
              }
              return (await response.json()) as Procedure;
            })
            : Promise.resolve(null),
        ]);

        if (!isMounted) {
          return;
        }

        if (!Number.isNaN(appointmentWhen.getTime())) {
          setAppointmentDate(formatEditableDate(appointmentWhen));
          setAppointmentStartTime(
            `${padTimePart(appointmentWhen.getHours())}:${padTimePart(appointmentWhen.getMinutes())}`
          );
        }
        setAppointmentDuration(formatMinutesAsTime(Number(appointment.duration) || 0));
        setHasManualDurationOverride(true);
        setAppointmentDescription(appointment.description ?? '');
        setExistingAppointmentStatus(appointment.status || 'unconfirmed');
        setSelectedPractitionerId(practitionerId);
        setSelectedPatient(patient);
        setEditingProcedureId(procedureId);
        setSelectedCategory(procedure?.category ?? '');
        setSelectedTreatment(procedure?.name?.trim() ?? '');
        localStorage.setItem('selectedPatientId', patientId);
        localStorage.setItem('selectedPractitionerId', practitionerId);
        localStorage.setItem('selectedProcedureId', procedureId);
      } catch (error) {
        console.error('Failed to load appointment for editing', error);
        if (isMounted) {
          setSaveErrorMessage(`Error: ${error instanceof Error ? error.message : String(error)}`);
        }
      } finally {
        if (isMounted) {
          setAppointmentLoading(false);
        }
      }
    };

    void fetchExistingAppointment();

    return () => {
      isMounted = false;
    };
  }, [selectedAppointmentId]);

  useEffect(() => {
    let isMounted = true;

    const fetchPractitioners = async () => {
      const token = localStorage.getItem('token');
      const cachedPractitioners = practitionerIds.reduce<Record<string, FullPractitioner>>((accumulator, practitionerId) => {
        const cachedPractitioner = practitionerCache.get(practitionerId);

        if (cachedPractitioner) {
          accumulator[practitionerId] = cachedPractitioner;
        }

        return accumulator;
      }, {});

      if (Object.keys(cachedPractitioners).length === practitionerIds.length) {
        setPractitionersById(cachedPractitioners);
        setPractitionersError('');
        setPractitionersLoading(false);
        return;
      }

      setPractitionersById(cachedPractitioners);
      setPractitionersLoading(true);
      setPractitionersError('');

      try {
        const missingPractitionerIds = practitionerIds.filter((practitionerId) => !practitionerCache.has(practitionerId));
        const fetchedPractitioners = await Promise.all(
          missingPractitionerIds.map(async (practitionerId) => {
            const response = await fetch(`${API_BASE_URL}/practitioners/full/${practitionerId}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (!response.ok) {
              throw new Error(`Bad practitioner response for ${practitionerId}: ${response.status}`);
            }

            const fullPractitioner = (await response.json()) as FullPractitioner;
            practitionerCache.set(practitionerId, fullPractitioner);

            console.log(practitionerId + ": " + JSON.stringify(fullPractitioner));

            return [practitionerId, fullPractitioner] as const;
          })
        );

        if (!isMounted) {
          return;
        }

        setPractitionersById({
          ...cachedPractitioners,
          ...Object.fromEntries(fetchedPractitioners),
        });
      } catch (error) {
        console.error('Failed to load practitioners', error);

        if (!isMounted) {
          return;
        }

        setPractitionersError('Behandler konnten nicht geladen werden.');
      } finally {
        if (isMounted) {
          setPractitionersLoading(false);
        }
      }
    };

    fetchPractitioners();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchProceduresByCategory = async () => {
      if (!selectedCategory || !selectedProcedureType) {
        setProcedures([]);
        setSelectedTreatment('');
        setTreatmentsLoading(false);
        setTreatmentsError('');
        return;
      }

      setTreatmentsLoading(true);
      setTreatmentsError('');
      setProcedures([]);
      setSelectedTreatment('');

      const token = localStorage.getItem('token');

      try {
        const response = await fetch(`${API_BASE_URL}/procedures/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ category: selectedCategory, type: selectedProcedureType }),
        });

        if (!response.ok) {
          throw new Error(`Bad procedure search response: ${response.status}`);
        }

        const resolvedProcedures = (await response.json()) as Procedure[];

        if (!isMounted) {
          return;
        }

        const nextProcedures = Array.isArray(resolvedProcedures) ? resolvedProcedures : [];
        setProcedures(nextProcedures);

        if (editingProcedureId) {
          const editingProcedure = nextProcedures.find((procedure) => procedure.id === editingProcedureId);
          if (editingProcedure) {
            setSelectedTreatment(editingProcedure.name.trim());
          }
        }
      } catch (error) {
        console.error('Failed to load procedures for category', error);

        if (!isMounted) {
          return;
        }

        setTreatmentsError('Behandlungen konnten nicht geladen werden.');
      } finally {
        if (isMounted) {
          setTreatmentsLoading(false);
        }
      }
    };

    fetchProceduresByCategory();

    return () => {
      isMounted = false;
    };
  }, [editingProcedureId, selectedCategory, selectedProcedureType]);

  useEffect(() => {
    if (!selectedProcedure || hasManualDurationOverride) {
      return;
    }

    const durationMinutes = Number(selectedProcedure.duration);
    if (Number.isNaN(durationMinutes) || durationMinutes < 0) {
      return;
    }

    setAppointmentDuration(formatMinutesAsTime(durationMinutes));
  }, [hasManualDurationOverride, selectedProcedure]);

  useEffect(() => {
    const parsedDate = parseDateInput(appointmentDate);
    const parsedStartMinutes = parseTimeToMinutes(appointmentStartTime);

    if (!parsedDate || parsedStartMinutes === null) {
      return;
    }

    const nextSelectedDateTime = new Date(parsedDate);
    nextSelectedDateTime.setHours(Math.floor(parsedStartMinutes / 60), parsedStartMinutes % 60, 0, 0);
    localStorage.setItem('selectedDateTime', formatLocalDateTime(nextSelectedDateTime));
  }, [appointmentDate, appointmentStartTime]);

  return (
    <div className="portal-page-surface admin-booking-page text-slate-800">
      <AppointmentResponseErrorDialog message={saveErrorMessage} onClose={() => setSaveErrorMessage('')} />
      <div className="admin-booking__card">
        <header>
          <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.02em] text-slate-900">
            {selectedAppointmentId ? 'Einzeltermin bearbeiten' : 'Einzeltermin erstellen'}
          </h1>
          {/* <p className="mt-2 text-base text-slate-500">
            {selectedAppointmentId
              ? 'Bearbeiten Sie die Details des bestehenden Patiententermins.'
              : 'Geben Sie die Details für den nächsten Patiententermin ein.'}
          </p> */}
        </header>

        <section className="admin-booking__time-row">
          <div className="flex flex-col gap-2.5">
            <div className="flex items-center gap-1.5">
              <label htmlFor="appointment-date" className="text-sm font-semibold tracking-[0.08em] text-slate-500">
                Datum
              </label>
              <button
                type="button"
                onClick={() => handleSelectAgendaSlot(selectedAppointmentId)}
                className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-500 transition hover:bg-[#faf8f4] hover:text-slate-700"
                aria-label="Termin in der Agenda planen"
              >
                <CalendarIcon />
              </button>
            </div>
            <input
              id="appointment-date"
              type="text"
              value={appointmentDate}
              onChange={(event) => setAppointmentDate(event.target.value)}
              onBlur={(event) => {
                const parsedDate = parseDateInput(event.target.value);
                if (parsedDate) {
                  setAppointmentDate(formatEditableDate(parsedDate));
                }
              }}
              className="h-13 rounded-2xl border border-[#efe7da] bg-white px-5 text-[17px] text-slate-700 outline-none transition focus:border-[#cbb58a]"
            />
          </div>

          <label className="flex flex-col gap-2.5">
            <span className="text-sm font-semibold tracking-[0.08em] text-slate-500">
              Beginn
            </span>
            <input
              type="text"
              value={appointmentStartTime}
              onChange={(event) => setAppointmentStartTime(event.target.value)}
              onBlur={(event) => setAppointmentStartTime(normalizeTimeInput(event.target.value))}
              className="h-13 rounded-2xl border border-[#efe7da] bg-white px-5 text-center text-[17px] text-slate-700 outline-none transition focus:border-[#cbb58a]"
            />
          </label>

          <label className="flex flex-col gap-2.5">
            <span className="text-sm font-semibold tracking-[0.08em] text-slate-500">
              Dauer
            </span>
            <input
              type="text"
              value={appointmentDuration}
              onChange={(event) => {
                setHasManualDurationOverride(true);
                setAppointmentDuration(event.target.value);
              }}
              onBlur={(event) => setAppointmentDuration(normalizeDurationInput(event.target.value))}
              className="h-13 rounded-2xl border border-[#efe7da] bg-white px-5 text-center text-[17px] text-slate-700 outline-none transition focus:border-[#cbb58a]"
            />
          </label>

          <label className="flex flex-col gap-2.5">
            <span className="text-sm font-semibold tracking-[0.08em] text-slate-500">
              Ende
            </span>
            <input
              type="text"
              value={computedEndTime}
              readOnly
              className="h-13 rounded-2xl border border-[#efe7da] bg-[#fbf9f5] px-5 text-center text-[17px] text-slate-700 outline-none"
            />
          </label>
        </section>

        <section className="admin-booking__patient">
          <div className="admin-booking__patient-field flex flex-col gap-2.5">
            <label htmlFor="single-appointment-patient" className="text-sm font-semibold tracking-[0.08em] text-slate-500">Patient</label>
            <AdminPatientSearch
              id="single-appointment-patient"
              selectedPatient={selectedPatient}
              onSelect={setSelectedPatient}
              onReset={() => setSelectedPatient(null)}
            />
          </div>

          <div className="admin-booking__patient-shortcuts">
            <SmallButton action="patient-latest">Letzte(r)</SmallButton>
            <SmallButton action="patient-newest">Neueste(r)</SmallButton>
          </div>
        </section>

        <section className="admin-booking__category-row">
          <DropdownField
            label="Kategorie"
            placeholderText="Kategorie auswählen"
            action="kategorie"
            items={categoryItems}
            value={selectedCategory}
            onChange={setSelectedCategory}
            getItemLabel={translateCategoryName}
          />
          <div className="admin-booking__practitioner">
            <DropdownField
              label="Behandler"
              compactValueLabel={[practitionersById[selectableSelectedPractitionerId]?.firstName, practitionersById[selectableSelectedPractitionerId]?.familyName]
                .map((name) => name?.trim().charAt(0) || '').join('')}
              placeholderText="Behandler auswählen"
              action="behandler"
              items={selectablePractitionerIds}
              value={selectableSelectedPractitionerId}
              onChange={setSelectedPractitionerId}
              disabled={practitionersLoading || selectablePractitionerIds.length === 0}
              getItemLabel={(practitionerId) => getPractitionerDisplayName(practitionersById[practitionerId]) || practitionerId}
              fullWidth
            />
            {practitionersLoading ? (
              <p className="mt-2 text-sm text-slate-500">Behandler werden geladen...</p>
            ) : null}
            {!practitionersLoading && practitionersError ? (
              <p className="mt-2 text-sm text-red-600">{practitionersError}</p>
            ) : null}
          </div>
        </section>

        <section className="admin-booking__treatment-row">
          <div>
            <DropdownField
              key={selectedCategory + '-' + treatmentItems.join('|')}
              label="Behandlung"
              placeholderText="Behandlung auswählen"
              action="behandlung"
              items={treatmentItems}
              value={selectedTreatment}
              onChange={(value) => {
                setSelectedTreatment(value);
                setHasManualDurationOverride(false);
              }}
              disabled={!selectedCategory || treatmentsLoading || treatmentItems.length === 0}
              getItemLabel={translateProcedureName}
              fullWidth
            />
          </div>
          <label className="flex flex-col gap-2.5">
            <span className="text-sm font-semibold tracking-[0.08em] text-slate-500">
              Beschreibung
            </span>
            <input
              type="text"
              value={appointmentDescription}
              placeholder="Beschreibung eingeben"
              onChange={(event) => setAppointmentDescription(event.target.value)}
              className="h-13 rounded-2xl border border-[#efe7da] bg-white px-5 text-[17px] text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#cbb58a]"
            />
          </label>
          {treatmentsLoading ? (
            <p className="md:col-span-2 text-sm text-slate-500">Behandlungen werden geladen...</p>
          ) : null}
          {!treatmentsLoading && treatmentsError ? (
            <p className="md:col-span-2 text-sm text-red-600">{treatmentsError}</p>
          ) : null}
          {!treatmentsLoading && !treatmentsError && selectedCategory && treatmentItems.length === 0 ? (
            <p className="md:col-span-2 text-sm text-slate-500">Keine Behandlungen für diese Kategorie gefunden.</p>
          ) : null}
        </section>

        <section hidden>
          <DropdownField placeholderText="Erweiterte Optionen" action="erweiterte-optionen" items={advancedOptionItems} fullWidth />
        </section>

        <section className="admin-booking__utilities">
          <div className="admin-booking__utilities-row">
            <div hidden>
              <p className="text-sm font-semibold tracking-[0.08em] text-slate-500">
                Status
              </p>
              <button
                type="button"
                onClick={() => placeholder('status')}
                className="mt-4 inline-flex h-11 items-center rounded-2xl bg-[#f1ede5] px-5 text-[17px] font-medium text-slate-700"
              >
                {existingAppointmentStatus === 'confirmed'
                  ? 'Bestätigt'
                  : existingAppointmentStatus === 'unconfirmed'
                    ? 'Unbestätigt'
                    : existingAppointmentStatus}
              </button>
            </div>

            <div>
              <p className="text-sm font-semibold tracking-[0.08em] text-slate-500">
                Terminkärtchen
              </p>
              <div className="admin-booking__utility-buttons">
                <UtilityButton
                  action="print"
                  icon={
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M7 9V4h10v5" />
                      <rect x="6" y="14" width="12" height="6" rx="1.5" />
                      <path d="M6 17H4.5A1.5 1.5 0 0 1 3 15.5v-4A1.5 1.5 0 0 1 4.5 10h15a1.5 1.5 0 0 1 1.5 1.5v4A1.5 1.5 0 0 1 19.5 17H18" />
                    </svg>
                  }
                >
                  Druck
                </UtilityButton>
                <UtilityButton
                  action="email"
                  icon={
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
                      <path d="m5.5 8 6.5 5 6.5-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  }
                >
                  Email
                </UtilityButton>
                <UtilityButton
                  action="sms"
                  icon={
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M5 6.5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-4 3v-3H5a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Z" />
                    </svg>
                  }
                >
                  SMS
                </UtilityButton>
              </div>
            </div>
          </div>
        </section>

        <div className="admin-booking__actions">
          <button
            type="button"
            onClick={() => navigate('/agenda')}
            hidden
            className="h-14 min-w-[220px] rounded-2xl border border-[#e7dfd2] bg-white px-8 text-[17px] font-semibold text-slate-700 transition hover:border-[#d7ccb8]"
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={appointmentLoading || isSaveDisabled}
            onClick={() => void saveAppointment()}
            className="admin-booking__save h-14 min-w-[220px] rounded-2xl bg-[#cbb58a] px-8 text-[17px] font-semibold text-white shadow-[0_12px_26px_rgba(203,181,138,0.3)] transition hover:bg-[#c5af82] disabled:cursor-not-allowed disabled:bg-[#d8c7a5] disabled:shadow-none"
          >
            {appointmentLoading ? 'Termin wird geladen…' : 'Termin speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
